import React, {
  type Dispatch,
  type SetStateAction,
  type RefObject,
} from "react";
import { useCallback, useMemo } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { useRenderProjectsShowing } from "hooks/useRenderProjectsShowing";
import { ProductListTable } from "./SubComponents/ProductListTable";
import { TablePagination } from "../TablePagination";
import type { SORTING_CRITERIA } from "variables";
import type { ProductListTableProduct } from "interfaces/product/product.types";

type ProductListOfProjectProps = {
  currentPageNo: number;
  fetchingProductList: boolean;
  fetchingPrices?: boolean;
  isOrderDescending: boolean;
  pagesCount: number;
  products: ProductListTableProduct[];
  productsCount: number;
  setCurrentPageNo: Dispatch<SetStateAction<number>>;
  setIsOrderDescending: Dispatch<SetStateAction<boolean>>;
  tableRef: RefObject<HTMLDivElement | null>;
  itemsPerPage: number;
  setSortCriteria: Dispatch<SetStateAction<string>>;
  sortCriteria: (typeof SORTING_CRITERIA)[keyof typeof SORTING_CRITERIA];
  isModalView: boolean;
  priceGroupDescription?: string;
};

export const ProductListOfProject = Scrivito.connect(
  ({
    currentPageNo,
    fetchingProductList,
    fetchingPrices = false,
    isOrderDescending,
    pagesCount,
    products,
    productsCount,
    setCurrentPageNo,
    setIsOrderDescending,
    tableRef,
    itemsPerPage,
    setSortCriteria,
    sortCriteria,
    isModalView,
    priceGroupDescription,
  }: ProductListOfProjectProps) => {
    const renderProjectsShowing = useRenderProjectsShowing();
    const shouldShowScroll = fetchingProductList || productsCount > 0;
    const skeletonCount = useMemo(() => {
      if (productsCount === 0) {
        return 10;
      }
      return +currentPageNo !== +pagesCount
        ? itemsPerPage
        : productsCount - itemsPerPage * (currentPageNo - 1);
    }, [currentPageNo, pagesCount, productsCount, itemsPerPage]);

    const isLastPage = useMemo(
      () => currentPageNo === pagesCount,
      [currentPageNo, pagesCount],
    );

    const handleSetCurrentPage = useCallback(
      (pageToSet: number) => {
        setCurrentPageNo(
          pageToSet > pagesCount ? pageToSet - 1 : pageToSet || 1,
        );
      },
      [setCurrentPageNo, pagesCount],
    );

    return (
      <>
        <div
          className={cx("w-100 m-0 d-flex row pb-0 overflow-auto", {
            "overflow-hidden": !shouldShowScroll,
          })}
          style={
            isModalView
              ? { maxHeight: "calc(70vh - 8rem)" }
              : { maxHeight: "650px" }
          }
        >
          <ProductListTable
            products={products}
            isOrderDescending={isOrderDescending}
            setIsOrderDescending={setIsOrderDescending}
            skeletonCount={skeletonCount}
            fetchingProductList={fetchingProductList}
            fetchingPrices={fetchingPrices}
            tableRef={tableRef}
            isLastPage={isLastPage}
            setSortCriteria={setSortCriteria}
            sortCriteria={sortCriteria}
            setCurrentPageNo={setCurrentPageNo}
            isModalView={isModalView}
            priceGroupDescription={priceGroupDescription}
          />
        </div>
        <div className="position-relative d-flex justify-content-center w-100 pt-1_5 pb-2_5 text-gray-700">
          <div className="position-absolute top-0 start-0 text-gray-400">
            {renderProjectsShowing(currentPageNo, productsCount, itemsPerPage)}
          </div>
          <TablePagination
            pagesCount={pagesCount}
            currentPage={currentPageNo}
            onChangePage={handleSetCurrentPage}
          />
        </div>
      </>
    );
  },
);
